import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BedDouble, Check, UserCheck, History, UserRound } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ExportButton } from "../components/ExportButton";
import { useSegnalazioni, SegnalazioneButton, SegnalazioniBadge } from "../components/Segnalazioni";
import { ConfirmUndoDialog } from "../components/ConfirmUndoDialog";
import { MeseGiorniEsplosi, RecapOperatoriMese, RecapGiorno } from "../components/MeseGiorniEsplosi";
import { UscitiInfoBox, isFuori, fuoriLabel } from "../components/UscitiInfoBox";
import { EsBadge } from "../components/EsBadge";
import { CorrettoBadge } from "../components/CorrettoBadge";
import { ElencoCorrezioniBtn, useCorrezioni } from "../components/ElencoCorrezioni";

const TIPO = "messa_a_letto";
const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };

export default function MessaALetto() {
  const navigate = useNavigate();
  const { selectedStructure, structures, can } = useAuth();
  const { activeOperatore, opName, canSign, signerName, signerId, mustUnlock } = useFirma();
  const qc = useQueryClient();
  const [confirmUndo, setConfirmUndo] = useState(null);
  const today = ymd(new Date());
  const [openDay, setOpenDay] = useState(today);
  const [mese, setMese] = useState(today.slice(0, 7));
  const selDate = openDay || today;
  const isToday = selDate === today;
  const yr = new Date().getFullYear();
  const [showStorico, setShowStorico] = useState(false);
  const [da, setDa] = useState(`${yr}-01-01`);
  const [a, setA] = useState(`${yr}-12-31`);

  const { data: meseData } = useQuery({
    queryKey: ["mal-mese", selectedStructure, mese],
    queryFn: async () => (await api.get("/alzate/mese", { params: { structure_id: selectedStructure, mese, tipo: TIPO } })).data,
  });
  const { data: storico } = useQuery({
    enabled: showStorico,
    queryKey: ["mal-storico", selectedStructure, da, a],
    queryFn: async () => (await api.get("/alzate/storico", { params: { structure_id: selectedStructure, da, a, tipo: TIPO } })).data,
  });
  const mLabel = (m) => new Date(`${m}-01`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  const { data } = useQuery({
    queryKey: ["mal", selectedStructure, selDate],
    queryFn: async () => (await api.get("/alzate", { params: { structure_id: selectedStructure, data: selDate, tipo: TIPO } })).data,
  });
  const ospiti = data?.ospiti || [];
  const records = data?.records || {};
  const operatori = data?.operatori || [];
  const { byKey: segByKey, aperte: segAperte, refetch: segRefetch } = useSegnalazioni("messa_a_letto", selectedStructure, selDate);
  const corrMap = useCorrezioni("messa_a_letto", selectedStructure, selDate.slice(0, 7));
  const canCorreggi = can("correggi_spunte");
  const totFatti = data?.tot_fatti ?? 0; const totOsp = data?.tot_ospiti ?? 0;
  const autonomiN = Object.values(records).filter((r) => r?.autonomo).length;
  const malStats = [
    { key: "aletto", label: "A letto", value: `${totFatti}/${totOsp}`, tone: totOsp && totFatti >= totOsp ? "ok" : "muted" },
    { key: "da-mettere", label: "Da mettere a letto", value: Math.max(0, totOsp - totFatti), tone: totOsp - totFatti > 0 ? "warn" : "ok" },
    { key: "autonomi", label: "Autonomi", value: autonomiN, tone: "muted" },
    ...(data?.completato_ora ? [{ key: "completato", label: "100% alle", value: data.completato_ora, tone: "ok" }] : []),
  ];

  const malColor = (structures || []).find((s) => s.id === selectedStructure)?.colore || "#4f46e5";
  const malName = selectedStructure === "all" ? "Tutte le strutture" : ((structures || []).find((s) => s.id === selectedStructure)?.nome || "");
  const malRows = ospiti.map((o) => { const r = records[o.ospite_id]; return { ospite: `${o.cognome} ${o.nome}`, camera: o.camera || "", stato: r ? "A letto" : "Da mettere a letto", operatore: r ? (r.operatore_nome || "") : "", ora: r ? (r.ora || "") : "" }; });
  const malCols = [
    { header: "Ospite", accessor: "ospite" },
    { header: "Camera/zona", accessor: "camera" },
    { header: "Stato", accessor: "stato" },
    { header: "Messo a letto da", accessor: "operatore" },
    { header: "Ora", accessor: "ora" },
  ];

  const toggle = async (o, autonomo = false) => {
    const rec = records[o.ospite_id];
    const done = !!rec;
    if (done) { if (!canSign) { toast.error("Nessun operatore attivo: sblocca con la Firma Rapida"); return; } }
    else if (!autonomo && !canSign) { toast.error("Nessun operatore attivo: sblocca con la Firma Rapida"); return; }
    if (done) { setConfirmUndo({ nome: `${o.cognome} ${o.nome}`, extra: "messa a letto", action: () => doToggle(o, autonomo) }); return; }
    doToggle(o, autonomo);
  };
  const doToggle = async (o, autonomo = false) => {
    try {
      const payload = { ospite_id: o.ospite_id, ospite_nome: `${o.nome} ${o.cognome}`, structure_id: o.structure_id, data: selDate, tipo: TIPO };
      if (autonomo && !records[o.ospite_id]) { payload.autonomo = true; payload.operatore_nome = "Autonomo"; }
      else { payload.operatore_id = signerId; payload.operatore_nome = signerName; }
      const r = await api.post("/alzate/register", payload);
      qc.invalidateQueries({ queryKey: ["mal"] });
      qc.invalidateQueries({ queryKey: ["correzioni"] });
      qc.invalidateQueries({ queryKey: ["mal-mese"] });
      qc.invalidateQueries({ queryKey: ["mal-storico"] });
      toast.success(r.data.done ? (autonomo ? "Registrato come autonomo" : `Messa a letto registrata (${r.data.ora})`) : "Registrazione annullata");
    } catch { toast.error("Errore"); }
  };

  return (
    <MeseGiorniEsplosi
      title="Messa a letto" subtitle="Spunta ogni ospite quando viene messo a letto · consulta e modifica anche i giorni passati (resta la firma dell'operatore)"
      Icon={BedDouble} color={malColor}
      mese={mese} onMese={setMese} openDay={openDay} onOpenDay={setOpenDay}
      counts={meseData?.counts || {}} countLabel="messe a letto"
      headerRight={<><SegnalazioniBadge categoria="messa_a_letto" aperte={segAperte} /><ElencoCorrezioniBtn categoria="messa_a_letto" structureId={selectedStructure} mese={mese} /></>}
      monthTop={<RecapOperatoriMese operatori={meseData?.operatori || []} color={malColor} label="messe a letto" icon={UserCheck} />}
      detailRight={<>
        <Button size="sm" variant={isToday ? "default" : "outline"} onClick={() => { setOpenDay(today); setMese(today.slice(0, 7)); }} data-testid="mal-oggi" className="font-bold">OGGI</Button>
        <Button size="sm" variant={showStorico ? "default" : "outline"} onClick={() => setShowStorico((v) => !v)} data-testid="mal-storico-btn"><History size={15} className="mr-1" /> Storico</Button>
        <ExportButton filename="messa-a-letto" title={`Messa a letto · ${selDate}`} subtitle={malName} columns={malCols} rows={malRows} color={malColor} testid="mal-export" />
      </>}
    >
      {showStorico && (
        <div className="gr-card p-5 mb-4" data-testid="mal-storico">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-end gap-2 flex-wrap">
              <div><label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Da</label><Input type="date" value={da} onChange={(e) => setDa(e.target.value)} className="w-auto" data-testid="mal-storico-da" /></div>
              <div><label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">A</label><Input type="date" value={a} onChange={(e) => setA(e.target.value)} className="w-auto" data-testid="mal-storico-a" /></div>
              <Button size="sm" variant="outline" onClick={() => { setDa(`${yr}-01-01`); setA(`${yr}-12-31`); }} data-testid="mal-storico-anno">Anno {yr}</Button>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Totale messe a letto periodo</div>
              <div className="text-3xl font-head font-bold text-primary" data-testid="mal-storico-totale">{storico?.totale ?? "—"}</div>
              <div className="text-xs text-muted-foreground mt-0.5">di cui autonomi: <b className="text-emerald-700" data-testid="mal-storico-autonomi">{storico?.autonomi ?? 0}</b></div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Per mese</div>
              {(storico?.mesi || []).length === 0 ? <p className="text-sm text-muted-foreground">Nessun dato.</p> : (storico.mesi.map((m) => (
                <div key={m.mese} className="flex items-center justify-between border-b py-1.5 text-sm" data-testid={`mal-storico-mese-${m.mese}`}>
                  <span className="capitalize font-medium">{mLabel(m.mese)}</span><span className="font-bold tabnum">{m.count}</span>
                </div>
              )))}
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Per operatore</div>
              {(storico?.operatori || []).length === 0 ? <p className="text-sm text-muted-foreground">Nessun dato.</p> : (storico.operatori.map((o) => (
                <div key={o.nome} className="flex items-center justify-between border-b py-1.5 text-sm" data-testid={`mal-storico-op-${o.nome}`}>
                  <span className="font-medium">{o.nome}</span><span className="font-bold tabnum text-primary">{o.count}</span>
                </div>
              )))}
            </div>
          </div>
        </div>
      )}

      <RecapGiorno operatori={operatori} stats={malStats} color={malColor} opLabel="messe a letto" />

      <UscitiInfoBox usciti={ospiti.filter(isFuori)} testid="mal-usciti-box" />

      {mustUnlock && <p className="text-sm text-amber-600 mb-4">⚠️ Sblocca con la Firma Rapida per registrare le messe a letto.</p>}

      <div className="space-y-2" data-testid="mal-list">
        {ospiti.length === 0 && <p className="text-sm text-muted-foreground">Nessun ospite</p>}
        {ospiti.map((o, i) => {
          const rec = records[o.ospite_id];
          const done = !!rec;
          const fuori = isFuori(o);
          const fuoriDal = o.fuori_dal || null;
          const fuoriOggi = o.fuori_giorno ?? (fuori && (!fuoriDal || selDate >= fuoriDal));
          const fuoriDalLbl = fuoriDal ? ` dal ${fuoriDal.slice(8, 10)}/${fuoriDal.slice(5, 7)}` : "";
          return (
            <div key={o.ospite_id} className={`rounded-xl border p-3 transition-colors ${fuoriOggi ? "bg-amber-50/60 border-amber-200" : done ? "border-emerald-300 bg-emerald-50" : fuori ? "bg-amber-50/40" : "bg-white"}`} data-testid={`mal-row-${o.ospite_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold sm:truncate flex items-center gap-2"><span className="shrink-0 grid place-items-center h-6 w-6 rounded-full bg-white/70 border text-muted-foreground text-[11px] font-bold tabnum">{i + 1}</span><span className="min-w-0 sm:truncate"><button type="button" onClick={() => navigate(`/ospiti/${o.ospite_id}`)} className="hover:underline text-left" data-testid={`mal-nome-${o.ospite_id}`}>{o.cognome} {o.nome}</button><EsBadge id={o.ospite_id} /><CorrettoBadge correzioni={corrMap[`${o.ospite_id}_${selDate}`]} testid={`mal-corretto-${o.ospite_id}`} />{o.camera && <span className="text-xs text-muted-foreground font-normal"> · Camera/zona {o.camera}</span>}</span>{fuori && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${o.presenza === "ospedale" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`mal-fuori-badge-${o.ospite_id}`}>{fuoriLabel(o)}{fuoriDalLbl}</span>}</div>
                  <div className="text-xs text-muted-foreground">{fuoriOggi ? <span className="text-amber-700 font-medium">Fuori struttura — non conteggiato{o.fuori_nota ? ` (${o.fuori_nota})` : ""}</span> : rec?.non_alzato ? <span className="text-emerald-700 font-medium" data-testid={`mal-non-alzato-nota-${o.ospite_id}`}>Già a letto — non è stato alzato oggi ({rec.motivo})</span> : done ? <span className="text-emerald-700 font-medium">{rec.autonomo ? "Autonomo" : "A letto"} · {rec.operatore_nome}{rec.ora ? ` · ${rec.ora}` : ""}</span> : "Da registrare"}{o.fuori_nota && !fuoriOggi && <span className="ml-1.5 text-amber-700 font-medium" data-testid={`mal-fuori-nota-${o.ospite_id}`}>· {o.fuori_nota}</span>}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                  {fuoriOggi ? (
                    <span className="h-11 sm:h-9 flex-1 sm:flex-none px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 bg-slate-200 text-slate-400 cursor-not-allowed" data-testid={`mal-fuori-${o.ospite_id}`}>Fuori struttura</span>
                  ) : rec?.non_alzato ? (
                    <span className="h-11 sm:h-9 flex-1 sm:flex-none px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200" title={`Non alzato: ${rec.motivo || ""}`} data-testid={`mal-gia-a-letto-${o.ospite_id}`}><Check size={16} /> Già a letto</span>
                  ) : done ? (
                    <Button variant="outline" disabled={!canSign} title={!canSign ? "Sblocca con la Firma Rapida per correggere" : ""} className="h-11 sm:h-9 flex-1 sm:flex-none text-emerald-700 border-emerald-300" onClick={() => toggle(o, true)} data-testid={`mal-toggle-${o.ospite_id}`}>
                      <Check size={16} className="mr-1" /> {rec.autonomo ? "Autonomo" : "A letto"}
                    </Button>
                  ) : (
                    <>
                      <button onClick={() => toggle(o)} disabled={!canSign} className="h-11 sm:h-9 flex-1 sm:flex-none px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 bg-white text-primary border border-primary/40 hover:bg-primary/5 transition-colors disabled:opacity-50" data-testid={`mal-toggle-${o.ospite_id}`}><BedDouble size={16} /> Segna a letto</button>
                      <button onClick={() => toggle(o, true)} title="Va a letto da solo/a, senza operatore" className="h-11 sm:h-9 flex-1 sm:flex-none px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 bg-secondary hover:bg-secondary/70 transition-colors" data-testid={`mal-auto-${o.ospite_id}`}><UserRound size={16} /> Da solo</button>
                    </>
                  )}
                  <SegnalazioneButton categoria="messa_a_letto" structureId={selectedStructure} data={selDate}
                    ospiteId={o.ospite_id} ospiteNome={`${o.cognome} ${o.nome}`}
                    targetOperatoreId={rec?.operatore_id || ""} targetOperatoreNome={rec?.operatore_nome || ""}
                    entry={segByKey[`${o.ospite_id}_${selDate}`]} onChanged={segRefetch} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <ConfirmUndoDialog ctx={confirmUndo} onClose={() => setConfirmUndo(null)} />
    </MeseGiorniEsplosi>
  );
}
